import React, { useState, useRef, useEffect, useCallback } from "react"
import peopleData from "../data/people.json"
import Image from "../components/image"
import { slugify } from "../helpers/strings"
import IconClose from "../images/close.inline.svg"
import Arrow from "../images/arrow.inline.svg"
import Modal from "react-modal"

export default function PeopleGrid({
  className,
  overlayActive,
  toggleOverlayVisibility,
}) {
  const [activeOverlayIndex, setActiveOverlayIndex] = useState(0)
  const [activeGridIndex, setActiveGridIndex] = useState(0)
  const [gridFocused, setGridFocus] = useState(false)
  const totalSlides = peopleData.length
  const forwardButton = useRef()
  const backButton = useRef()
  const peopleGrid = useRef()

  const toggleOverlay = id => {
    setActiveOverlayIndex(id)
    toggleOverlayVisibility()
  }

  const overlayGoBack = useCallback(() => {
    const previous =
      activeOverlayIndex === 0 ? peopleData.length - 1 : activeOverlayIndex - 1
    setActiveOverlayIndex(previous)
  }, [activeOverlayIndex])

  const overlayGoForward = useCallback(() => {
    const next =
      activeOverlayIndex === peopleData.length - 1 ? 0 : activeOverlayIndex + 1
    setActiveOverlayIndex(next)
  }, [activeOverlayIndex])

  const gridGoBack = useCallback(() => {
    const previous =
      activeGridIndex === 0 ? peopleData.length - 1 : activeGridIndex - 1
    setActiveGridIndex(previous)
    const activeElement = document.activeElement.className
    if (activeElement === "people-grid__grid__button") {
      peopleGrid.current.children[previous].children[0].focus()
    }
  }, [activeGridIndex])

  const gridGoForward = useCallback(() => {
    const next =
      activeGridIndex === peopleData.length - 1 ? 0 : activeGridIndex + 1
    setActiveGridIndex(next)
    const activeElement = document.activeElement.className
    if (activeElement === "people-grid__grid__button") {
      peopleGrid.current.children[next].children[0].focus()
    }
  }, [activeGridIndex])

  const downHandler = useCallback(
    ({ key }) => {
      if ((key === "ArrowLeft" || key === "Left") && overlayActive) {
        overlayGoBack()
        backButton.current.focus()
      }

      if ((key === "ArrowRight" || key === "Right") && overlayActive) {
        overlayGoForward()
        forwardButton.current.focus()
      }

      if ((key === "ArrowRight" || key === "Right") && !overlayActive) {
        gridGoForward()
      }

      if ((key === "ArrowLeft" || key === "Left") && !overlayActive) {
        gridGoBack()
      }

      if ((key === "Escape" || key === "Esc") && overlayActive) {
        toggleOverlayVisibility()
      }
    },
    [
      overlayGoBack,
      overlayGoForward,
      gridGoBack,
      gridGoForward,
      toggleOverlayVisibility,
      overlayActive,
    ]
  )

  const showGridInstructions = () => {
    let userIsTabbing = document.body.classList.contains("user-is-tabbing")
    if (userIsTabbing) {
      setGridFocus(true)
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", downHandler)
    // Remove event listeners on cleanup
    return () => {
      document.removeEventListener("keydown", downHandler)
    }
  }, [overlayActive, activeOverlayIndex, downHandler])

  const getAlternatesText = alternatesArray => {
    if (alternatesArray.length === 1) {
      return "Alternate"
    } else {
      return "Alternates"
    }
  }

  return (
    <>
      <section className={`people-grid ${className}`}>
        <div
          className={
            gridFocused
              ? `people-grid__instructions people-grid__instructions--focused`
              : `people-grid__instructions`
          }
          aria-live="polite"
        >
          {gridFocused &&
            "Use the left and right arrow keys on your keyboard to navigate"}
        </div>

        <div role="grid">
          <div role="row" className="people-grid__grid" ref={peopleGrid}>
            {peopleData.map((person, index) => (
              <span role="gridcell" key={index}>
                <button
                  onClick={() => toggleOverlay(index)}
                  tabIndex={index === activeGridIndex ? 0 : -1}
                  onFocus={() => showGridInstructions()}
                  onBlur={() => setGridFocus(false)}
                  className="people-grid__grid__button"
                >
                  <figure>
                    <Image
                      path={`who-we-are/people/${slugify(person.name)}.jpg`}
                      alt={`Photo of ${person.name}`}
                    />
                    <figcaption>
                      {person.title && (
                        <span className="title--micro title--light">
                          {person.title}
                        </span>
                      )}
                      {person.name}
                    </figcaption>
                  </figure>
                </button>
              </span>
            ))}
          </div>
        </div>
      </section>

      <Modal
        isOpen={overlayActive}
        overlayClassName="overlay people-grid__overlay"
        className="overlay__content people-grid__overlay__content"
        bodyOpenClassName="util__body-lock"
      >
        <button
          className="overlay__button overlay__button--close"
          onClick={() => toggleOverlayVisibility()}
          aria-label="Close overlay"
          aria-controls="overlay"
          aria-hidden={overlayActive ? "false" : "true"}
        >
          <IconClose />
        </button>

        <div className="people-grid__overlay__inner">
          <div className="people-grid__overlay__info">
            <Image
              path={`who-we-are/people/${slugify(
                peopleData[activeOverlayIndex].name
              )}.jpg`}
              alt={peopleData[activeOverlayIndex].name}
            />
            <div className="people-grid__overlay__info__inner">
              <h3>{peopleData[activeOverlayIndex].name}</h3>
              <h4>
                {getAlternatesText(
                  peopleData[activeOverlayIndex].alternates.split(",")
                )}
              </h4>
              <p>{peopleData[activeOverlayIndex].alternates}</p>
              <h4>Contituency</h4>
              <p>{peopleData[activeOverlayIndex].contituency}</p>
            </div>
          </div>
        </div>

        <div
          className="people-grid__overlay__controls"
          aria-hidden={overlayActive ? "false" : "true"}
        >
          <button
            className="overlay__previous overlay__button"
            onClick={() => overlayGoBack()}
            ref={backButton}
            aria-label="Go to previous slide"
          >
            <Arrow />
          </button>

          <div className="people-grid__overlay__controls__count">
            {activeOverlayIndex + 1} of {totalSlides}
          </div>

          <button
            aria-label="Go to next slide"
            className="overlay__next overlay__button"
            ref={forwardButton}
            onClick={() => overlayGoForward()}
          >
            <Arrow />
          </button>
        </div>
      </Modal>
    </>
  )
}
